import { DatePickerValue, InputDatePicker } from '@dixa/lib-shared-habla';
import type { SearchFilterValue } from 'lib-search-types';
import { ampli } from 'lib-shared-tracking';
import moment, { Moment } from 'moment-timezone';
import { useCallback, useEffect, useMemo } from 'react';

import { ConditionFieldDateProps } from '../conditionFieldDate';
import { maxDate, timePickerProps } from '../utils/datepickerProps';

/** Represents a single date filter */
export const ConditionFieldDateSingle = ({
  condition,
  disabled,
  onChange,
}: ConditionFieldDateProps): JSX.Element => {
  /** Validates the condition values. */
  const isInvalid = useMemo((): boolean => {
    const [dateValue] = condition.values ?? [];

    // Handles the case where switching from a range to a single date filter component,
    // where we clear the selected value if it was previously a range, or a preset.
    return dateValue
      ? condition.values.length > 1 || !moment(dateValue.value).isValid()
      : false;
  }, [condition.values]);

  /** If the incoming condition value is invalid, we clear it. */
  useEffect(() => {
    if (isInvalid) {
      onChange([]);
    }
  }, [condition.values, isInvalid, onChange]);

  /** The date value passed down to the datepicker component. */
  const value = useMemo((): Moment | null => {
    const [dateValue] = condition.values ?? [];
    return !dateValue || isInvalid ? null : moment(dateValue.value);
  }, [condition.values, isInvalid]);

  // Checks if the selected date includes a custom time (not 12 AM which is the default)
  const hasTime = useCallback((date: Moment | null) => {
    return date ? date.format('HH:mm') !== '00:00' : false;
  }, []);

  /** Updates the condition with the new date value. */
  const handleChange = useCallback(
    ({ date }: DatePickerValue) => {
      const value: SearchFilterValue = {
        label: date?.format('L LT') ?? '',
        value: date?.toISOString() ?? '',
      };

      onChange([value]);

      ampli.searchFilterValueAdded({
        type: condition.field,
        operator: condition.operator,
        value: hasTime(date) ? 'dateWithCustomTime' : 'dateWithDefaultTime',
      });
    },
    [condition, hasTime, onChange],
  );

  // Adjust the date format and placeholder used to display the selected value depending on whether a custom time is selected
  const datePlaceholder = moment.localeData().longDateFormat('L');
  const timePlaceholder = hasTime(value)
    ? moment.localeData().longDateFormat('LT')
    : '';
  const dateFormat = [datePlaceholder, timePlaceholder].join(' ').trim();

  return (
    <InputDatePicker
      value={value}
      disabled={disabled}
      maxDate={maxDate}
      dateFormat={dateFormat}
      placeholder={dateFormat.toLowerCase()}
      datePickerProps={{
        inputsPlacement: 'top',
        withDateInput: true,
        withTimePicker: true,
        timePickerProps,
      }}
      dropdownProps={{
        fixed: true,
      }}
      onChange={handleChange}
    />
  );
};
